import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { Form, Select } from 'antd5';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { WorkContainerChooser } from 'components/choosers/WorkContainerChooser';
import { getTaskTargetInput } from 'components/CreateTask/helpers';
import { Button, ModalActionButtons, MultiLineErrorMessage, useMessage } from 'components/ui';
import {
  formatDamageId,
  getPrimaryObservationForDamage,
  getPrimaryObservationGroupForDamage,
} from 'horizon/components/Damages/utils';
import {
  type TaskStep,
  ApplyTaskTemplateStepChooser,
} from 'horizon/routes/WorkOrders/ApplyTaskTemplateButton/ApplyTaskTemplateStepChooser';
import { useWCNotificationSubscriptionPrompt } from 'horizon/routes/WorkOrders/NotificationSubscriptions';
import { TaskTemplatesChooser } from 'horizon/routes/WorkOrders/Tasks/TaskTemplates/TaskTemplatesChoosers';
import {
  type AtlasGqlCreateTaskFromTemplateMutation,
  type AtlasGqlCreateTaskMutation,
  type AtlasGqlCreateTasksFromTemplateMutation,
  type AtlasGqlCreateTasksMutation,
  type AtlasGqlDamage,
  type AtlasGqlDamageInspectTask,
  type AtlasGqlDamageRepairTask,
  type AtlasGqlHorizonDamage,
  type AtlasGqlTask,
  AtlasGqlWorkContainerTypeOption,
  useCreateTasksFromTemplateMutation,
  useCreateTasksMutation,
} from 'types/atlas-graphql';
import { type ApolloCache } from 'utils/apollo';
import { WORK_ORDER } from 'utils/constants';

interface ICreateTasksFromDamagesForm {
  damages: (AtlasGqlHorizonDamage | AtlasGqlDamage)[];
  open: boolean;
  setOpen: (open: boolean) => void;
}

type FormValues = {
  taskType:
    | AtlasGqlWorkContainerTypeOption.RepairTask
    | AtlasGqlWorkContainerTypeOption.InspectTask;
  taskTemplate: string | undefined;
  workOrder: string | undefined;
};

type AtlasGqlDamageTaskTypes = AtlasGqlDamageRepairTask | AtlasGqlDamageInspectTask;

export const CreateTasksFromDamagesForm: React.FC<ICreateTasksFromDamagesForm> = ({
  damages = [],
  open,
  setOpen,
}) => {
  const { t } = useTranslation();
  const { openPrompt } = useWCNotificationSubscriptionPrompt();
  const message = useMessage();
  const [disabled, setDisabled] = useState<boolean>(true);
  const [taskType, setTaskType] = useState<AtlasGqlWorkContainerTypeOption[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string | undefined>(undefined);
  const [selectedTaskSteps, setSelectedTaskSteps] = useState<TaskStep[]>([]);
  const [form] = Form.useForm();
  const messageKeySuccess = 'createTasksFromDamagesSuccessMessage';
  const messageKeyError = 'createTasksFromDamagesErrorMessage';
  const isHorizonDamage = damages[0]?.__typename === 'HorizonDamage';

  const handleReset = () => {
    form.resetFields();
    setDisabled(true);
    setTaskType([]);
    setSelectedTemplate(undefined);
    setSelectedTaskSteps([]);
    setOpen(false);
  };

  const messageContent = ({ type, count }: { type: string; count: number }) => {
    const workOrderId: string | undefined = form.getFieldValue('workOrder');
    const typeLabel =
      type === AtlasGqlWorkContainerTypeOption.RepairTask
        ? t('inspection.repairTaskType', {
            count,
            defaultValue_one: 'Repair task',
            defaultValue_other: 'Repair tasks',
          })
        : t('inspection.inspectTaskType', {
            count,
            defaultValue_one: 'Inspect task',
            defaultValue_other: 'Inspect tasks',
          });
    return (
      <a
        id="task-create-success-message"
        href={workOrderId ? `/work-orders/${workOrderId}` : '/unplanned-tasks'}
      >
        {t('inspection.tasksCreatedClickToView', {
          count,
          typeLabel,
          defaultValue_one: '{{count}} {{typeLabel}} created. Click to View.',
          defaultValue_other: '{{count}} {{typeLabel}} created. Click to View.',
        })}
      </a>
    );
  };

  const handleComplete = (
    data:
      | AtlasGqlCreateTasksFromTemplateMutation['createTasksFromTemplate']
      | AtlasGqlCreateTasksMutation['createTasks']
  ) => {
    // Filter out any null values in data
    const dataLength = data?.filter(Boolean).length;
    if (dataLength) {
      const type = form.getFieldValue('taskType');
      openPrompt({
        content: messageContent({ type, count: dataLength }),
        workContainer: data[0] as AtlasGqlTask,
        containerIdsForBulkSubscription: data.map(task => task?.id).filter(Boolean),
      });
      message.destroy(messageKeySuccess);
    } else {
      message.success({
        content: t(
          'inspection.createTasksDataIssue',
          'Creating Tasks finished, but there was an issue returning the data.'
        ),
        key: messageKeySuccess,
        duration: 3,
      });
    }
    handleReset();
  };

  const handleError = (error: unknown) => {
    const errors = CombinedGraphQLErrors.is(error) ? error.errors : [];

    let errorMessage = (
      <p>{t('inspection.createTasksIssue', 'There was an issue with creating Tasks')}</p>
    );

    const damageNumbers: string[] = [];
    errors.forEach(err => {
      // We should be able to pull the number of the damage as well. Check for it first though:
      const damageId = isHorizonDamage ? err.extensions?.horizonDamageId : err.extensions?.damageId;
      const selectedDamage = damages.find(damage => damage.id === damageId);

      if (selectedDamage) {
        const damageNumber = isHorizonDamage
          ? formatDamageId(selectedDamage as AtlasGqlHorizonDamage)
          : (selectedDamage as AtlasGqlDamage)?.number;

        if (damageNumber) {
          damageNumbers.push(damageNumber);
        }
      }
    });

    if (damageNumbers.length > 0) {
      errorMessage = MultiLineErrorMessage({
        messageContent: t(
          'inspection.createTasksDamagesIssue',
          'There was an issue creating Tasks for the following Damages: {{damageNumbers}}',
          { damageNumbers: damageNumbers.join(', ') }
        ),
      });
    }

    message.error({
      content: errorMessage,
      key: messageKeyError,
      duration: 0,
      style: {
        maxWidth: '600px',
        margin: 'auto',
      },
      icon: <></>,
      className: 'create-task-error-message',
    });

    handleReset();
  };

  useEffect(() => {
    // When modal is closed by clicking outside of it reset the form
    return () => {
      if (!open) {
        form.resetFields();
      }
    };
  }, [open]);

  const handleWriteFragment = (
    cache: ApolloCache<AtlasGqlCreateTaskFromTemplateMutation | AtlasGqlCreateTaskMutation>,
    data: AtlasGqlDamageTaskTypes[]
  ) => {
    data.forEach(task => {
      const taskId = `${task.__typename}:${task.id}`;
      const damageId = isHorizonDamage
        ? `${task.horizonDamage?.__typename}:${task.horizonDamage?.id}`
        : `${task.damage?.__typename}:${task.damage?.id}`;

      // Append task ref to the damage ID's task array
      cache.modify({
        id: damageId,
        fields: {
          tasks(cachedTasks: AtlasGqlDamageTaskTypes[]) {
            return [...cachedTasks, { _ref: taskId }];
          },
        },
      });
    });
  };

  const [
    createTasksFromTemplate,
    {
      data: createTasksFromTemplateData,
      error: createTasksFromTemplateError,
      loading: createTasksFromTemplateLoading,
    },
  ] = useCreateTasksFromTemplateMutation({
    errorPolicy: 'all',
  });

  // Due to how partial successes WITH errors works we need to handle completed/errors within this useEffect:
  useEffect(() => {
    if (!createTasksFromTemplateLoading) {
      if (createTasksFromTemplateData?.createTasksFromTemplate) {
        handleComplete(createTasksFromTemplateData.createTasksFromTemplate);
      }
      if (createTasksFromTemplateError) {
        handleError(createTasksFromTemplateError);
      }
    }
  }, [createTasksFromTemplateLoading, createTasksFromTemplateData, createTasksFromTemplateError]);

  const [
    createTasks,
    { data: createTasksData, error: createTasksError, loading: createTasksLoading },
  ] = useCreateTasksMutation({
    errorPolicy: 'all',
  });

  // Due to how partial successes WITH errors works we need to handle completed/errors within this useEffect:
  useEffect(() => {
    if (!createTasksLoading) {
      if (createTasksData?.createTasks) {
        handleComplete(createTasksData.createTasks);
      }
      if (createTasksError) {
        handleError(createTasksError);
      }
    }
  }, [createTasksLoading, createTasksData, createTasksError]);

  const onFinish = async (values: FormValues) => {
    setDisabled(true);
    message.loading({
      content: t('inspection.creatingTasks', 'Creating tasks...'),
      key: messageKeySuccess,
      duration: 0,
    });
    const { taskType, workOrder, taskTemplate } = values;

    const damagesInputs = damages.map(damage => {
      const primaryLegacyDamageBranchId = isHorizonDamage
        ? getPrimaryObservationForDamage(damage as AtlasGqlHorizonDamage)?.legacyDamageBranchId
        : null;
      const inspectionId = isHorizonDamage
        ? getPrimaryObservationGroupForDamage(damage as AtlasGqlHorizonDamage)?.inspectionId
        : null;

      return {
        assetId: damage.assetId ?? damage.asset?.id,
        parentId: workOrder ? workOrder : null,
        ...(isHorizonDamage
          ? {
              damageId: primaryLegacyDamageBranchId ?? undefined,
              damageBranchId: primaryLegacyDamageBranchId ?? undefined,
              horizonDamageId: damage.id,
              targets: getTaskTargetInput({ inspectionId, horizonDamageId: damage.id }),
            }
          : {
              damageId: damage.id,
              damageBranchId: (damage as AtlasGqlDamage).branchGlobalId,
            }),
      };
    });

    if (taskTemplate) {
      const taskStepNames = selectedTaskSteps.map(s => s.name);
      await createTasksFromTemplate({
        variables: {
          templateId: taskTemplate,
          inputs: damagesInputs.map(input => ({
            ...input,
            taskStepNames,
          })),
        },
        update: (cache, { data }) => {
          if (data?.createTasksFromTemplate) {
            const { createTasksFromTemplate } = data;
            // Errors on individual tasks will create nulls.
            handleWriteFragment(
              cache,
              createTasksFromTemplate.filter(Boolean) as AtlasGqlDamageTaskTypes[]
            );
          }
        },
      });
    } else {
      await createTasks({
        variables: {
          inputs: damagesInputs.map(input => ({
            ...input,
            type: taskType,
          })),
        },
        update: (cache, { data }) => {
          if (data?.createTasks) {
            const { createTasks } = data;
            // Errors on individual tasks will create nulls within our data.
            handleWriteFragment(cache, createTasks.filter(Boolean) as AtlasGqlDamageTaskTypes[]);
          }
        },
      });
    }
  };

  const onValuesChange = (changedValues: FormValues) => {
    if (changedValues.taskType) {
      setDisabled(false);
      setTaskType([changedValues.taskType]);
      setSelectedTemplate(undefined);
      setSelectedTaskSteps([]);
    }
  };

  return (
    <Form
      name="createTasksOnDamages"
      form={form}
      layout="vertical"
      initialValues={{ taskType: undefined, taskTemplate: undefined, workOrder: undefined }}
      onFinish={onFinish}
      onValuesChange={onValuesChange}
    >
      <Form.Item
        name="taskType"
        label={t('inspection.taskType', 'Task Type')}
        rules={[
          {
            required: true,
            message: t('inspection.selectTaskTypeRequired', 'Please select a task type.'),
          },
        ]}
      >
        <Select placeholder={t('inspection.selectTaskTypePlaceholder', 'Select a Task Type')}>
          <Select.Option value={AtlasGqlWorkContainerTypeOption.RepairTask}>
            {t('workOrder.repair', 'Repair')}
          </Select.Option>
          <Select.Option value={AtlasGqlWorkContainerTypeOption.InspectTask}>
            {t('inspection.inspectTaskOption', 'Inspect Task')}
          </Select.Option>
        </Select>
      </Form.Item>
      <Form.Item name="taskTemplate" label={t('inspection.template', 'Template')}>
        <TaskTemplatesChooser
          displayType="select"
          onClickHandler={(templateId: string) => {
            form.setFieldsValue({ taskTemplate: templateId });
            setSelectedTemplate(templateId);
            setSelectedTaskSteps([]);
          }}
          workContainerTypes={taskType}
          disabled={Boolean(!taskType.length)}
        />
      </Form.Item>
      {selectedTemplate && (
        <ApplyTaskTemplateStepChooser
          templateId={selectedTemplate}
          selectedTaskSteps={selectedTaskSteps}
          setSelectedTaskSteps={setSelectedTaskSteps}
          showTopDivider={true}
          showBottomDivider={true}
        />
      )}
      <Form.Item name="workOrder" label={t('workOrder.title', 'Work Order')}>
        <WorkContainerChooser
          type={WORK_ORDER}
          allowClear={true}
          containerId="damages-work-order-chooser"
        />
      </Form.Item>
      <ModalActionButtons>
        <Button onClick={handleReset}>{t('common.cancel', 'Cancel')}</Button>
        <Button type="primary" htmlType="submit" disabled={disabled}>
          {t('common.create', 'Create')}
        </Button>
      </ModalActionButtons>
    </Form>
  );
};
